import { useState } from 'react';
import {
  Autocomplete,
  Box, createFilterOptions, FilterOptionsState, InputAdornment, TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import SearchIcon from '@mui/icons-material/Search';
import {
  darkGold, warmGold, straightFont, secondary,
} from '../../Constants';
import './SearchBar.css';
import { getAllRecipes } from '../../services/DbService';
import IRecipe from '../../interfaces/IRecipe';
import useRouter from '../../hooks/use-router';
import { useTheme } from '../../providers/ThemeProvider';
import { getRecipeUrl } from '../../utils/seo';
import { normalizeIngredient } from '../../utils/ingredientHelpers';

export default function SearchBar() {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const router = useRouter();
  const { mode, colors } = useTheme();

  const { data: allRecipes, error } = useQuery(
    ['recipes'], // Standardized key - shares cache with other pages
    getAllRecipes,
  );

  const baseFilter = createFilterOptions<IRecipe>({
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    stringify: (option) => {
      // Search across multiple fields for better results
      const tagString = option.tags?.join(' ') || '';
      const keywordsString = option.keywords?.join(' ') || '';
      const ingredientsString = option.ingredients
        ?.map((ing) => normalizeIngredient(ing).name || '')
        .join(' ') || '';
      const description = option.description || '';
      const cuisine = option.cuisine || '';
      const category = option.category || '';

      return `${option.title} ${tagString} ${keywordsString} ${ingredientsString} ${description} ${cuisine} ${category}`;
    },
  });

  const filterOptions = (options: IRecipe[], state: FilterOptionsState<IRecipe>) => {
    const input = state.inputValue.trim();
    if (!input) return [];
    return baseFilter(options, { ...state });
  };

  if (error) {
    return <div>Error loading recipes</div>;
  }

  if (!allRecipes) {
    return <div>Loading recipes...</div>;
  }

  return (
    <Box sx={{
      width: '100%', maxWidth: 600, margin: '0 auto', py: '16px',
    }}
    >
      <Autocomplete
        freeSolo
        id="searchBar"
        disableClearable
        options={allRecipes}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue || '');
        }}
        onChange={(event, newValue) => {
          if (newValue && typeof newValue === 'object' && 'id' in newValue) {
            router.push(getRecipeUrl(newValue as IRecipe));
            setInputValue('');
          }
        }}
        filterOptions={filterOptions}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.title || '';
        }}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              borderRadius: '8px',
              transition: 'background-color 0.2s ease, transform 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = mode === 'light' ? '#f8f6f2' : 'rgba(255,255,255,0.08)';
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <img
              src={option.image}
              alt={option.title}
              width={36}
              height={36}
              style={{
                objectFit: 'cover',
                borderRadius: '8px',
                marginRight: 12,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            />
            <Typography sx={{ fontFamily: straightFont, color: colors.text }}>
              {option.title}
            </Typography>
          </li>
        )}
        componentsProps={{
          paper: {
            sx: {
              backgroundColor: colors.cardBackground,
              color: colors.text,
              border: '1px solid',
              borderColor: colors.border,
              boxShadow: mode === 'light'
                ? '0 4px 12px rgba(0,0,0,0.1)'
                : '0 4px 12px rgba(0,0,0,0.4)',
              borderRadius: '12px',
              mt: 1,
            },
          },
        }}
        sx={{
          backgroundColor: mode === 'light' ? 'rgba(255,255,255,0.7)' : colors.cardBackground,
          boxShadow: mode === 'light' ? '0 2px 6px rgba(0,0,0,0.06)' : '0 2px 6px rgba(0,0,0,0.3)',
          transition: 'all 0.3s ease',
          borderRadius: '12px',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: mode === 'light' ? warmGold : secondary,
            },
            '&:hover fieldset': {
              borderColor: mode === 'light' ? darkGold : secondary,
            },
            '&.Mui-focused fieldset': {
              borderColor: secondary,
            },
            '& .MuiInputLabel-root': {
              color: colors.text,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.text,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="What are you craving?"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            sx={{
              color: colors.text,
              fontFamily: straightFont,
              '& .MuiOutlinedInput-root': {
                color: colors.text,
                '& fieldset': {
                  borderColor: mode === 'light' ? warmGold : secondary,
                },
                '&:hover fieldset': {
                  borderColor: secondary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: secondary,
                },
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
              },
              '& label.Mui-focused': {
                color: secondary,
              },
            }}
            InputProps={{
              ...params.InputProps,
              type: 'search',
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: secondary }} />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              shrink: isFocused || inputValue !== '',
              sx: {
                color: colors.textSecondary,
                marginLeft: isFocused ? 0 : '36px',
              },
            }}
          />
        )}
      />
    </Box>
  );
}
